import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ArrowLeft } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Edward.");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-navy text-cream flex items-center justify-center px-4 grain-overlay relative">
      <div className="w-full max-w-md">
        <Link
          to="/"
          data-testid="login-back-link"
          className="inline-flex items-center gap-2 font-jbmono text-xs uppercase tracking-widest text-cream/50 hover:text-gold transition-colors duration-200 mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to site
        </Link>
        <div className="bg-midnight border border-steel/60 rounded-lg p-8 sm:p-10">
          <div className="flex flex-col items-center text-center gap-4 mb-2">
            <img src="/images/master-blaster-logo.png" alt="The Master Blaster logo" className="h-16 w-auto rounded-md" />
            <h1 className="font-display font-black uppercase text-2xl tracking-tight">Owner Login</h1>
          </div>
          <p className="text-sm text-cream/50 mb-8">Pre-order leads dashboard. Authorized patriots only.</p>
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <label htmlFor="email" className="font-jbmono text-xs uppercase tracking-widest text-cream/70">Email</label>
              <input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-navy border border-steel rounded-sm px-4 py-3 text-sm text-cream placeholder:text-cream/30 focus:outline-none focus:border-crimson transition-colors duration-200"
                placeholder="you@masterblaster.com"
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="font-jbmono text-xs uppercase tracking-widest text-cream/70">Password</label>
              <input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-navy border border-steel rounded-sm px-4 py-3 text-sm text-cream placeholder:text-cream/30 focus:outline-none focus:border-crimson transition-colors duration-200"
                placeholder="••••••••"
                required
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-crimson font-medium">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="dashboard-login-button"
              className="w-full flex items-center justify-center gap-2 bg-crimson hover:bg-crimsondark disabled:opacity-60 text-white font-display font-black uppercase tracking-wide text-lg px-6 py-4 rounded-sm transition-colors duration-200"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              {loading ? "Verifying…" : "Enter Dashboard"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
